/**
 * ============================================================================
 * useEquipmentDetailQuery — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for one equipment's full dossier — header,
 *          timeline of every lifecycle event, qualification chain
 *          (URS → DQ → FAT → SAT → IQ → OQ → PQ → REQUAL), calibration
 *          history, PM records, requalification rows, document cross-refs
 *          and specs.
 * Endpoint: GET /api/v1/equipment/{id}
 *
 * NOTE on payloads: per-event `payload`, per-calibration `measurements`,
 *  and per-PM `parts_replaced` are modeled as `Record<string, unknown>`
 *  (or array of). Narrow at the call site.
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import type {
  EquipmentCriticality,
  EquipmentLifecycleStatus,
  GampClass,
} from '../EquipmentWorkbench/useEquipmentQuery';

export type EquipmentEventKind =
  | 'CREATED'
  | 'URS_APPROVED'
  | 'DQ_APPROVED'
  | 'FAT_PASSED'
  | 'SAT_PASSED'
  | 'IQ_PASSED'
  | 'OQ_PASSED'
  | 'PQ_PASSED'
  | 'QUALIFIED'
  | 'CALIBRATION_RECORDED'
  | 'PM_RECORDED'
  | 'LOCATION_CHANGED'
  | 'LIFECYCLE_TRANSITION'
  | 'RESTRICTED'
  | 'REQUALIFICATION_STARTED'
  | 'REQUALIFICATION_COMPLETED'
  | 'RETIRED'
  | 'DECOMMISSIONED'
  | 'OTHER';

export interface EquipmentTimelineEvent {
  event_id: string;
  ts_utc: string;
  kind: EquipmentEventKind;
  actor: string | null;
  title: string;
  description: string;
  /** Per-event extras. */
  payload: Record<string, unknown>;
}

export type QualificationStepKey =
  | 'URS'
  | 'DQ'
  | 'FAT'
  | 'SAT'
  | 'IQ'
  | 'OQ'
  | 'PQ'
  | 'REQUAL';

export type QualificationStepState =
  | 'PASSED'
  | 'FAILED'
  | 'PENDING'
  | 'CURRENT'
  | 'MISSING';

export interface QualificationStep {
  key: QualificationStepKey;
  state: QualificationStepState;
  signed_at_utc: string | null;
  document_id: string | null;
  signer: string | null;
}

export type CalibrationOutcome = 'PASSED' | 'FAILED' | 'OUT_OF_TOLERANCE';

export type CalibrationSource = 'INTERNAL' | 'EXTERNAL_VENDOR';

export interface CalibrationRecord {
  calibration_id: string;
  performed_at_utc: string;
  source: CalibrationSource;
  outcome: CalibrationOutcome;
  technician: string;
  supervisor: string | null;
  vendor_supplier_id: string | null;
  /** Corrective re-cal link (for FAILED / OUT_OF_TOLERANCE re-tries). */
  correction_of_calibration_id: string | null;
  measurements: Record<string, unknown>;
}

export type PMType =
  | 'ROUTINE'
  | 'CORRECTIVE'
  | 'PREVENTIVE'
  | 'BREAKDOWN_REPAIR';

export type PMOutcome = 'COMPLETED' | 'PARTIAL' | 'DEFERRED' | 'FAILED';

export interface PMRecord {
  pm_id: string;
  performed_at_utc: string;
  pm_type: PMType;
  outcome: PMOutcome;
  engineer: string;
  doc_controller_signer: string | null;
  work_description: string;
  parts_replaced: Array<Record<string, unknown>>;
}

export type RequalificationTrigger =
  | 'CALENDAR'
  | 'CHANGE_CONTROL'
  | 'DEVIATION'
  | 'LOCATION_CHANGE';

export type RequalificationState = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';

export interface RequalificationRow {
  requalification_id: string;
  trigger: RequalificationTrigger;
  scheduled_for_utc: string;
  state: RequalificationState;
  started_at_utc: string | null;
  completed_at_utc: string | null;
  validation_lead: string | null;
  qualification_document_id: string | null;
}

export interface CrossRefDoc {
  document_id: string;
  document_number: string;
  title: string;
  quarantined_at_utc: string;
  state: string;
}

export interface EquipmentSpecs {
  manufacturer: string | null;
  model: string | null;
  serial: string | null;
  category_key: string | null;
  calibration_cadence_days: number;
  pm_cadence_days: number;
  requalification_cadence_days: number;
  location: string | null;
  site_code: string | null;
  /** Cadence override extras (e.g. site-specific). */
  cadence_overrides: Record<string, unknown>;
}

export interface EquipmentDetail {
  equipment_id: string;
  equipment_tag: string;
  name: string;
  criticality: EquipmentCriticality;
  gamp_class: GampClass;
  lifecycle_status: EquipmentLifecycleStatus;
  last_calibration_at_utc: string | null;
  last_calibration_outcome: CalibrationOutcome | null;
  next_calibration_at_utc: string | null;
  next_pm_at_utc: string | null;
  next_requalification_at_utc: string | null;
  timeline: EquipmentTimelineEvent[];
  qualification_chain: QualificationStep[];
  calibrations: CalibrationRecord[];
  pm_records: PMRecord[];
  requalifications: RequalificationRow[];
  cross_refs: CrossRefDoc[];
  specs: EquipmentSpecs;
}

export function useEquipmentDetailQuery(
  equipmentId: string,
): UseQueryResult<EquipmentDetail> {
  return useQuery<EquipmentDetail>({
    queryKey: ['equipment', equipmentId, 'detail'],
    enabled: !!equipmentId,
    queryFn: ({ signal }) =>
      apiFetch<EquipmentDetail>(
        `/api/v1/equipment/${encodeURIComponent(equipmentId)}`,
        { signal },
      ),
  });
}
